import { Component, inject, OnDestroy, OnInit, signal, Signal, ViewEncapsulation } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations'; // <-- Importa animaciones de Angular
import { AsyncPipe, NgFor, NgForOf, NgIf } from "@angular/common";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { IntegrationErrorsBadgeComponent } from 'app/layout/common/integration-errors-badge/integration-errors-badge.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { BreadcrumbService } from 'app/services/common/breadcrum.service';
import { Subject, takeUntil } from 'rxjs';
import { FuseLoadingService } from '@fuse/services/loading';
import { fuseAnimations } from '@fuse/animations';
import { ExportService } from 'app/layout/export-overlay/export-overlay.service';
import { SearchPageComponent } from 'app/layout/common/search-page/search-page.component';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('fadeInOut', [
            state('void', style({ opacity: 0 })), // Estado inicial
            state('*', style({ opacity: 1 })),    // Estado visible
            transition(':enter', [
                animate('300ms ease-in'),         // Duración de entrada
            ]),
            transition(':leave', [
                animate('300ms ease-out'),        // Duración de salida
            ]),
        ]),
        fuseAnimations,
    ],
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        SearchPageComponent,
        ShortcutsComponent,
        IntegrationErrorsBadgeComponent,
        MessagesComponent,
        RouterOutlet,
        QuickChatComponent,
        NgFor,
        NgForOf,
        NgIf,
        AsyncPipe,
    ],

})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    filterOpened: boolean = false;
    showSpinner = false;  // Controla cuándo mostrar el spinner
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    breadcrumbs: Signal<any[]> = signal<any[]>([]);
    private _fuseLoadingService = inject(FuseLoadingService);
    protected isActive = false;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private breadcrumbService: BreadcrumbService,
        protected readonly exportService: ExportService,
    ) {
    }

    ngOnInit(): void {
        // Escucha los eventos de navegación para mostrar/ocultar el spinner
        this._router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.showSpinner = true; // Muestra el spinner
                this._fuseLoadingService.show();
            }
            if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                this.showSpinner = false; // Oculta el spinner
                this._fuseLoadingService.hide();
            }
        });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
            });

            this.breadcrumbs = this.breadcrumbService.breadcrumbs;
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            navigation.toggle();
        }
    }

    onExportButtonPressed() {
        this.exportService.setVisible(true);
    }

    /**
     * Toggles the filter panel.
     * 
     * This method toggles the visibility of the filter panel by updating the `filterOpened` property
     * and attempts to find and control the FilterComponent in the current route's component.
     * Since the filter component is rendered in child components via router-outlet,
     * we need to search for it in the DOM and access its Angular component instance.
     */
    toggleFilter(): void {
        this.filterOpened = !this.filterOpened;
        
        // Find the app-filter component in the current page
        const filterElement = document.querySelector('app-filter') as HTMLElement;
        
        if (!filterElement) return;

        // Try to get the Angular component instance using a more reliable method
        const componentInstance = this.getAngularComponent(filterElement);
            
        if (componentInstance && typeof componentInstance.togglePanel === 'function') {
            // Call the togglePanel method directly on the component instance
            componentInstance.togglePanel();
        } else {
            // Fallback: simulate a click on the close button within the filter component
            const closeButton = filterElement.querySelector('#filter-close-button') as HTMLElement;
            if (closeButton) {
                closeButton.click();
            }
        }

        // Notify search component about filter panel state change
        setTimeout(() => {
            this.notifySearchComponent();
        }, 100);
    }

    /**
     * Notify search component about filter panel state change
     */
    private notifySearchComponent(): void {
        const searchComponent = document.querySelector('search-page') as any;
        if (searchComponent && searchComponent.updateFilterPanelState) {
            searchComponent.updateFilterPanelState();
        }
    }

    /**
     * Gets the Angular component instance from a DOM element.
     * This is a more robust way to access Angular component instances.
     */
    private getAngularComponent(element: HTMLElement): any {
        // Try multiple methods to get the component instance
        const ngContext = (element as any).__ngContext__;
        if (ngContext && ngContext[0]) {
            return ngContext[0];
        }
        
        // Alternative method using Angular's internal APIs
        const ngElement = element as any;
        if (ngElement._ngElementContext) {
            return ngElement._ngElementContext;
        }
        
        return null;
    }
}

